/**
 * Constantes de presentación del subdominio **Precios del cotizador**.
 *
 * Etiquetas, metadatos de las secciones/tablas y mensajes de error de respaldo.
 * El gating de la UI usa el permiso `all` (ya en `ADMIN_PERMISSION` de
 * `constants.ts`), igual que el SSR (`@require_admin`) y la API
 * (`@api_require_permission("all")`).
 */
import type { UpdatePreciosBody } from "./precios.types";

/** Claves escalares (numéricas simples) de la configuración de precios. */
export type PrecioEscalarKey =
  | "precio_kg_venta"
  | "costo_kg_compra"
  | "factor_utilidad"
  | "iva"
  | "perfiles"
  | "cantidad"
  | "dobles_interno_general"
  | "dobles_refuerzo"
  | "dobles_por_tubo"
  | "dobles_division";

/** Etiqueta de cada campo escalar (texto del SSR `admin_precios_puertas.html`). */
export const ESCALAR_LABEL: Record<PrecioEscalarKey, string> = {
  precio_kg_venta: "Precio kg venta",
  costo_kg_compra: "Costo kg compra",
  factor_utilidad: "Factor utilidad",
  iva: "IVA (ej. 1.19)",
  perfiles: "Perfiles",
  cantidad: "Cantidad (valor)",
  dobles_interno_general: "Jamba / cabezal",
  dobles_refuerzo: "Refuerzo",
  dobles_por_tubo: "Tubos 7×8",
  dobles_division: "División (6/8)",
};

/** Escalares de la Sección 1 (lámina e impuestos), en orden del SSR. */
export const SECCION_LAMINA: PrecioEscalarKey[] = [
  "precio_kg_venta",
  "costo_kg_compra",
  "factor_utilidad",
  "iva",
];

/** Escalares de la columna izquierda de la Sección 2 (perfiles/cantidad). */
export const SECCION_DOBLES_IZQUIERDA: PrecioEscalarKey[] = ["perfiles", "cantidad"];

/** Escalares de la columna derecha de la Sección 2 (doblez por tipo). */
export const SECCION_DOBLES_DERECHA: PrecioEscalarKey[] = [
  "dobles_interno_general",
  "dobles_refuerzo",
  "dobles_por_tubo",
  "dobles_division",
];

/** Claves de la Sección 3 que son tablas por calibre (dict calibre -> pares). */
export type TablaCalibreKey =
  | "precios_cabezal"
  | "precios_jamba"
  | "precios_refuerzo"
  | "precios_division_6"
  | "precios_division_8";

/** Metadato (clave + título) de las 5 tablas por calibre, en orden del SSR. */
export const TABLAS_CALIBRE: { key: TablaCalibreKey; title: string }[] = [
  { key: "precios_cabezal", title: "Cabezal" },
  { key: "precios_jamba", title: "Jamba" },
  { key: "precios_refuerzo", title: "Refuerzo" },
  { key: "precios_division_6", title: "División 6" },
  { key: "precios_division_8", title: "División 8" },
];

/** Clave de la tabla simple (lista plana sin calibre). */
export const TABLA_SIMPLE_KEY = "precios_tubo_remarco" satisfies keyof UpdatePreciosBody;

/** Título de la tabla simple. */
export const TABLA_SIMPLE_TITLE = "Tubos 7×8";
